import { combineRefs } from '@visiba/react-std';
import { ChangeEvent, ForwardedRef, forwardRef } from 'react';
import { Controller, type RegisterOptions } from 'react-hook-form';
import { useText } from '../../../utils/hooks/use_text';
import { useFormContext } from '../form';
import { InputPropsHelper } from '../form/form.helpers';
import { CellulaInputSelectGroup } from './_input_select_group.component';
import { CellulaInputSelectItem } from './_input_select_item.component';
import { CellulaInputSelectRoot, InputSelectRootProps } from './_input_select_root.component';
import { CellulaInputSelectSeparator } from './_input_select_separator.component';

interface InputSelectProps extends InputSelectRootProps {
	options?: RegisterOptions;
}

function CellulaInputSelect(props: InputSelectProps, ref: ForwardedRef<HTMLButtonElement>): JSX.Element {
	// biome-ignore lint/correctness/useHookAtTopLevel: false positive - this is a component
	const formContext = useFormContext();
	// biome-ignore lint/correctness/useHookAtTopLevel: false positive - this is a component
	const staticTexts = useText();

	if (formContext == null || props.name == null) {
		return <CellulaInputSelectRoot {...props} ref={ref} />;
	}

	function handleChange(event: ChangeEvent<HTMLSelectElement>): void {
		props.onValueChange?.(event.target.value);
	}

	return (
		<Controller
			control={formContext.control.control}
			name={props.name}
			defaultValue={props.defaultValue}
			rules={{
				...props.options,
				onChange: handleChange,
			}}
			disabled={props.disabled}
			render={({ field, fieldState, formState }) => {
				const inputPropsHelper = new InputPropsHelper(props, staticTexts, fieldState, formState);

				function handleValueChange(value: string): void {
					field.onChange(value);
				}

				return (
					<CellulaInputSelectRoot
						{...props}
						{...field}
						optional={inputPropsHelper.isOptional()}
						required={inputPropsHelper.isRequired()}
						invalid={inputPropsHelper.isInvalid()}
						invalidText={inputPropsHelper.invalidText()}
						readOnly={inputPropsHelper.isReadOnly()}
						value={field.value ?? ''}
						onValueChange={handleValueChange}
						defaultValue={undefined}
						ref={combineRefs([ref, field.ref])}
					/>
				);
			}}
		/>
	);
}

export const InputSelect = {
	Root: forwardRef(CellulaInputSelect),
	Item: forwardRef(CellulaInputSelectItem),
	Group: forwardRef(CellulaInputSelectGroup),
	Separator: forwardRef(CellulaInputSelectSeparator),
} as const;

export type { InputSelectProps };
